import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { Box, Grid, Typography } from "@mui/material";
import "../../components-styles.scss";
import "../../Dossiers/Dossier/dossier-styles.scss";
import {
    OverViewWidgetItem,
    Session,
    DossierStatusEnum,
    establishementStatusEnum,
    ClientDashBoardNavBarMenuItemId,
} from "../../../types";
import { LayoutBarContext } from "../../../contexts";

import ReactSvgIcon from "../../UIElements/ReactSvgIcon";
import { ReactComponent as QualifactionIcon } from "../../../public/icons/QualifactionIcon.svg";
import { ReactComponent as DossierIcon } from "../../../public/icons/activeIcons/ActiveDossierIcon.svg";
import OverViewWidgets from "../../OverViewWigets/OverViewWidgets";
import HomeHeader from "../../Common/HomeHeader/HomeHeader";
import Dossiers from "../../Dossiers/Dossiers";
import { useDossier } from "../../Dossiers/useDossier";
import OverViewDossierWrapper from "../../Dossiers/Dossier/OverViews/OverViewDossierWrapper";
import { dossierStorageKey, sessionStorageKey } from "../../../consts";
import { useLocalStorage } from "../../../hooks";
import { getEstablishmentStatusByCode } from "../../Dossiers/utils";
import EmptyDossiers from "../../Dossiers/Dossier/EmptyDossiers/EmptyDossiers";
import Documents from "../../Documents/Documents";
import { qualificationFilterMenuItem } from "../../Documents/Documents";
import { extractNumber } from "../../../helpers";

export default function Home() {
    const { updateContent, setActive } = useContext(LayoutBarContext);

    const { storedValue: session } =
        useLocalStorage<Session>(sessionStorageKey);

    const {
        auth: { user: currentUser },
        [dossierStorageKey]: { establishmentStatus },
    } = session;

    const {
        establishements,
        currentDossiers,
        _fetchCurrentDossiers,
        downloadCertificate,
        regionList,
        fetchRegionList,
    } = useDossier();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    // retiré
    const withdrawEstablishmentStatus = getEstablishmentStatusByCode(
        establishementStatusEnum.RETIRE,
        establishmentStatus
    );

    const selfEstablishment = establishements.find(
        (e) => e.id === currentUser.establishment
    );

    const checkIFDossierIsFinalized = (d: any) => {
        return (
            d.dossierStatus.code === DossierStatusEnum.FINALISE ||
            d.establishmentStatus === withdrawEstablishmentStatus?.id
        );
    };

    const canCreateNewDossier = () => {
        /**
         * Règles :
         * 1 - aucun dossier en cours d'examen (c'est à dire n'a pas de status finalisé ou retiré)
         * 2 - aucun dossir en base de données
         */

        const havingCurrentDossier = currentDossiers.filter(
            (d) => !checkIFDossierIsFinalized(d)
        );

        return !havingCurrentDossier.length || !currentDossiers.length;
    };

    const getTopLevelRefCodeof = (dossiers: any[]) => {
        const refCodeNumbers = dossiers.map((d) =>
            extractNumber(d.referenciel.code)
        );
        const maxRefCodeNumber = Math.max(...refCodeNumbers);

        const topLevelFolder = dossiers.find(
            (d) =>
                extractNumber(d.referenciel.code) === maxRefCodeNumber &&
                checkIFDossierIsFinalized(d)
        );

        return {
            refCodeNumber: maxRefCodeNumber,
            finalized: topLevelFolder ? true : false,
        };
    };

    const overViewWidgetItems: OverViewWidgetItem[] = useMemo(
        () => [
            {
                icon: (
                    <ReactSvgIcon
                        width="1.5rem"
                        height="1.5rem"
                        ReactIcon={DossierIcon}
                    />
                ),
                title: "Mes dossiers",
                onClick: () => {
                    updateContent(<Dossiers />);
                    setActive(ClientDashBoardNavBarMenuItemId.DOSSIERS);
                },
            },
            {
                icon: (
                    <ReactSvgIcon
                        width="1.5rem"
                        height="1.5rem"
                        ReactIcon={QualifactionIcon}
                    />
                ),
                title: "Mes qualifications",
                onClick: () => {
                    updateContent(
                        <Documents
                            initialFilterMenuItem={qualificationFilterMenuItem}
                        />
                    );
                    setActive(ClientDashBoardNavBarMenuItemId.DOCUMENTS);
                },
            },
        ],
        []
    );

    const fetchCurrentDossiers: any = useCallback(async () => {
        await _fetchCurrentDossiers();
        setIsLoading(true);
    }, []);

    useEffect(() => {
        fetchCurrentDossiers();
    }, [fetchCurrentDossiers]);

    useEffect(() => {
        fetchRegionList();
    }, [fetchRegionList]);

    return (
        <>
            <header className="dossier-header">
                <Box
                    sx={{
                        marginLeft: "6rem",
                        marginTop: "5vh",
                        marginRight: "1.5rem",
                        marginBottom: "2rem",
                        display: "flex",
                        flexDirection: "column",
                        gap: "2.28rem",
                        "@media (max-width:1024px)": {
                            marginLeft: "1.5rem",
                        },
                    }}
                >
                    <HomeHeader
                        title={`Bienvenue sur le compte SOCOTEC de ${
                            selfEstablishment
                                ? selfEstablishment?.companyName
                                : ""
                        }`}
                    />
                </Box>
            </header>
            <main className="layout-body" style={{ overflow: "hidden" }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "3rem",
                        marginLeft: "6rem",
                        marginRight: "6rem",
                        marginTop: "2rem",
                        width: "100%",
                        "@media (max-width:1024px)": {
                            marginRight: "1.5rem",
                            marginLeft: "1.5rem",
                            width: "100vh",
                            marginBottom: "5rem",
                        },
                        "@media (min-width: 600px) and (max-width:1024px)": {
                            width: "100vw",
                        },
                    }}
                >
                    <Grid container spacing={1.5}>
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "1.5rem",
                                width: "100%",
                            }}
                        >
                            {isLoading &&
                            !canCreateNewDossier() &&
                            currentDossiers.length ? (
                                <>
                                    <Typography
                                        sx={{
                                            fontFamily: "Roboto",
                                            fontSize: "1.5rem",
                                            fontStyle: "normal",
                                            fontWeight: 500,
                                            lineHeight: "normal",
                                            color: "#023",
                                        }}
                                    >
                                        Dossiers
                                    </Typography>
                                    {currentDossiers.map((cd: any) => {
                                        return (
                                            <OverViewDossierWrapper
                                                key={cd.id}
                                                dossierDetail={cd}
                                                downloadCertificate={
                                                    downloadCertificate
                                                }
                                                regionList={regionList}
                                            />
                                        );
                                    })}
                                </>
                            ) : isLoading && canCreateNewDossier() ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        gap: "0.5rem",
                                        alignSelf: "stretch",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: "#023",

                                            fontFamily: "Roboto",
                                            fontSize: "1.5rem",
                                            fontStyle: "normal",
                                            fontWeight: 500,
                                            lineHeight: "normal",
                                        }}
                                    >
                                        Dossiers en cours
                                    </Typography>
                                    <EmptyDossiers
                                        topLevelRefCode={
                                            currentDossiers.length
                                                ? getTopLevelRefCodeof(
                                                      currentDossiers
                                                  )
                                                : undefined
                                        }
                                    />
                                </Box>
                            ) : null}
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "1.5rem",
                                width: "100%",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: "Roboto",
                                    fontSize: "1.5rem",
                                    fontStyle: "normal",
                                    fontWeight: 500,
                                    lineHeight: "normal",
                                    color: "#023",
                                }}
                            >
                                Vue d’ensemble
                            </Typography>
                            <OverViewWidgets
                                overViewWidgetItems={overViewWidgetItems}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </main>
        </>
    );
}
